import React, { Component } from 'react';
import { connect } from 'react-redux';
import { getFormTemplates } from 'actions/templates';
import { translate } from '@haldor/ui';
import Modal from 'containers/Modals/Modal';
import FormTemplate from 'containers/Forms/FormTemplate';
import SelectMultiple from 'containers/Overview/TableFilterComponents/SelectMultiple';
import { Spinner } from 'UI';
import { setPageTitle, setPageActions } from 'actions/header';
import { DataGridPro, GridToolbarContainer } from '@mui/x-data-grid-pro';
import { Box, Chip } from '@mui/material';

const STATUS_KEYS = ['OPEN', 'DRAFT', 'ARCHIVED'];
const FormTemplateAny = /** @type {any} */ (FormTemplate);

class AllForms extends Component {

	constructor(props) {
		super(props);

		this.state = {
			modal: false,
			loading: true,
			statusFilter: ['OPEN', 'DRAFT'],
		};
	}

	UNSAFE_componentWillMount = () => {
		let actions = [];

		this.props.setPageTitle(this.props.translate('form-templates'));

		actions.push({
			type: 'button',
			value: this.props.translate('Create'),
			onClick: this.toggleModal,
			icon: 'plus',
		});

		this.props.setPageActions(actions);
	}

	componentDidMount = async () => {
		await this.props.getFormTemplates();
		this.setState({ loading: false });
	}

	toggleModal = (reload) => {
		this.setState({ modal: !this.state.modal });

		if (reload) {
			this.props.getFormTemplates();
		}
	}

	handleStatusFilterChange = (selectedOptions = []) => {
		this.setState({
			statusFilter: selectedOptions.map((option) => option.id),
		});
	}

	render() {
		const { translate } = this.props;
		const normalizeStatus = (status = '') => (status === 'PUBLISHED' ? 'OPEN' : status);
		const getStatusLabel = (status = '') => {
			const normalizedStatus = normalizeStatus(status);
			if (normalizedStatus === 'OPEN') return 'Published';
			if (normalizedStatus === 'DRAFT') return 'Draft';
			if (normalizedStatus === 'ARCHIVED') return 'Archived';
			return status;
		};

		const statusOptions = STATUS_KEYS.map((status) => ({
			id: status,
			title: translate(getStatusLabel(status))
		}));

		const selectedStatusOptions = statusOptions.filter((option) => this.state.statusFilter.includes(option.id));
		const filteredRows = (this.props.templates || []).filter((row) => this.state.statusFilter.includes(normalizeStatus(row.status)));

		const formatStatus = (status = '') => {
			return status.toLowerCase().charAt(0).toUpperCase() + status.toLowerCase().slice(1);
		};

		const columns = [
			{
				field: 'title',
				headerName: translate('Title'),
				flex: 1.5,
				minWidth: 180,
				sortable: true,
			},
			{
				field: 'created',
				headerName: translate('Created'),
				flex: 0.9,
				minWidth: 150,
				renderCell: (p) => p.row?.created ? new Date(p.row.created).toLocaleDateString() : '',
			},
			{
				field: 'creator',
				headerName: translate('Created by'),
				flex: 0.9,
				minWidth: 140,
				renderCell: (p) => {
					const user = p.row?.creatorUser;
					return user ? `${user.firstName} ${user.lastName}` : '';
				},
				valueGetter: (value, row) => row?.creatorUser,
				sortComparator: (v1, v2) => {
					if (!v1 && !v2) return 0;
					if (!v1) return 1;
					if (!v2) return -1;

					const firstNameCompare = (v1.firstName || '').localeCompare(v2.firstName || '', 'sv');
					if (firstNameCompare !== 0) return firstNameCompare;

					return (v1.lastName || '').localeCompare(v2.lastName || '', 'sv');
				}
			},
			{
				field: 'edited',
				headerName: translate('Archived'),
				flex: 0.9,
				minWidth: 150,
				sortable: true,
				renderCell: (p) =>
					p.row?.status === 'ARCHIVED' && p.row?.edited
						? new Date(p.row.edited).toLocaleDateString()
						: ''
			},
			{
				field: 'editor',
				headerName: translate('Archived by'),
				flex: 0.9,
				minWidth: 140,
				sortable: true,
				renderCell: (p) => {
					if (p.row?.status !== 'ARCHIVED') return '';

					const user = p.row?.editorUser;
					return user ? `${user.firstName} ${user.lastName}` : '';
				},
				valueGetter: (value, row) => {
					return row?.status === 'ARCHIVED' ? row?.editorUser : null;
				},
				sortComparator: (v1, v2) => {
					if (!v1 && !v2) return 0;
					if (!v1) return 1;
					if (!v2) return -1;

					const firstNameCompare = (v1.firstName || '').localeCompare(v2.firstName || '', 'sv');
					if (firstNameCompare !== 0) return firstNameCompare;

					return (v1.lastName || '').localeCompare(v2.lastName || '', 'sv');
				}
			},
			{
				field: 'status',
				headerName: translate('Information'),
				flex: 0.7,
				minWidth: 150,
				sortable: true,
				renderCell: (p) => {
					const status = normalizeStatus(p.row?.status || '');

					return (
						<Chip
							label={translate(getStatusLabel(status) || formatStatus(status))}
							size="small"
							sx={{
								bgcolor: 'rgba(0, 0, 0, 0.08)',
								color: 'text.primary',
								borderRadius: '5px'
							}}
						/>
					);
				}
			}
		];

		return (
			<div className="overview">
				<Modal isOpen={this.state.modal} onClose={() => this.toggleModal(false)} title={translate('create-template')}>
					<FormTemplateAny onAbort={this.toggleModal} />
				</Modal>

				{this.state.loading ?
					<Spinner center />
					: null}

				{this.props.templates != null && this.props.templates.length > 0 && !this.state.loading ?
					<Box>
						<DataGridPro
							rows={filteredRows}
							columns={columns}
							getRowId={(r) => r.id}
							disableColumnMenu
							disableRowSelectionOnClick
							pagination
							pageSizeOptions={[15]}
							initialState={{
								sorting: { sortModel: [{ field: 'created', sort: 'desc' }] },
								pagination: { paginationModel: { pageSize: 15 } },
							}}
							slots={{
								toolbar: () => (
									<GridToolbarContainer sx={{ p: 1, bgcolor: '#fff' }}>
										<SelectMultiple
											label={translate('Status')}
											options={statusOptions}
											initialSelectedItems={selectedStatusOptions}
											onChange={this.handleStatusFilterChange}
											sx={{ minWidth: 300, maxWidth: 360 }}
										/>
									</GridToolbarContainer>
								)
							}}
							onRowClick={(params, event) => {
								if (event.detail === 0) return;

								const selection = window.getSelection();
								if (selection && selection.toString().length > 0) return;

								this.props.history.push(`/forms/template/${params.id}`);
							}}
							sx={{
								'& .MuiDataGrid-columnHeaderTitle': {
									fontWeight: 'bold'
								},
								'& .MuiDataGrid-main': {
									paddingLeft: '1rem',
									paddingRight: '1rem',
									backgroundColor: '#fff'
								},
								'& .MuiDataGrid-footerContainer': {
									backgroundColor: '#fff'
								},
								'& .MuiDataGrid-row': {
									cursor: 'pointer'
								}
							}}
						/>
					</Box>
					: null}
			</div>
		);
	}

}

function mapStateToProps(state) {
	return {
		services: state.Services.availableServices,
		translate: translate(state.Languages.translations),
		templates: state.Templates.templates,
	};
}

export default connect(mapStateToProps, {
	setPageTitle,
	setPageActions,
	getFormTemplates,
})(AllForms);
